import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { create } from "zustand";

import { ApiError, api, background } from "../../api/client";
import type { BBox, Exercise, StorageUsage, Zone, ZoneAxis, ZoneCreated, ZoneDeleted, ZoneInterruption } from "../../api/types";
import { designateSoon } from "../../components/attention";
import { t, tServer } from "../../i18n/i18n";
import { type Notice, useUi } from "../../state/ui";
import { actionLabel, GENERATE_TARGET, takeDeclineAfterTerrain } from "../../views/declination/declination";
import { appliedWeatherLabel, CUSTOM, SEASONAL, weatherGap, weatherGapText, weatherKey, weatherOptionsKey, weatherState } from "../../weather";
import {
  becameFailed,
  becameReady,
  becameSuperseded,
  failureCounter,
  pollDelay,
  retryUntil,
  zoneBusy,
  zoneWait,
} from "./zoneState";
import { useZoneDraw } from "./zoneDraw";

/** Requêtes et écritures des zones libres de la licence (docs/14 §4.3), relevé de la zone du dossier ouvert pendant
 *  sa préparation (§2.7.5 : toutes les 1,5 s, au niveau de l'application, où que soit l'utilisateur) et espace de
 *  stockage de la licence. */

export const ZONES_KEY = ["zones"] as const;
export const zoneKey = (id: string) => ["zone", id] as const;
export const STORAGE_KEY = ["storage"] as const;

/** Cibles `data-attention` de la carte de zone (désignées depuis la carte, la barre latérale et les avis). */
export const ZONE_TARGETS = {
  card: "zone-card",
  progress: "zone-progress",
  retry: "zone-retry",
  remove: "zone-delete",
  extend: "zone-extend",
  climate: "zone-climate",
  // Réglage « Météo de l'exercice » de l'étape Terrain (sous le climat, ou sous la ligne du camp).
  weather: "zone-weather",
  storage: "storage-free",
  trace: "zone-trace",
  search: "zone-search",
  recreate: "zone-recreate",
} as const;

/** Demande de dépliage du réglage « Météo de l'exercice » de l'étape Terrain (« Régler la météo » d'un avis) : il se
 *  déplie à son montage ou aussitôt, dans les secondes qui suivent. */
const OPEN_REQUEST_MS = 3000;
export const useTerrainWeatherRequest = create<{ at: number }>(() => ({ at: 0 }));

export function openRequested(now = Date.now()): boolean {
  return now - useTerrainWeatherRequest.getState().at < OPEN_REQUEST_MS;
}

/** Vue Manœuvre sur le réglage « Météo de l'exercice », déplié et désigné. */
export function showTerrainWeather(): void {
  useTerrainWeatherRequest.setState({ at: Date.now() });
  useUi.getState().setView("manoeuvre");
  designateSoon(`[data-attention="${ZONE_TARGETS.weather}"]`);
}

/** Zones libres de la licence. `poll` (accueil) : relue toutes les 5 s tant que l'une se prépare, pour que les
 *  pastilles des dossiers non ouverts avancent aussi (la zone du dossier ouvert est relevée à part, en 1,5 s). */
export function useZoneList(enabled = true, poll = false) {
  return useQuery({
    queryKey: ZONES_KEY,
    queryFn: () => background(api.zones),
    enabled,
    staleTime: 30_000,
    refetchInterval: poll ? (query) => (query.state.data?.some(zoneBusy) && !query.state.error ? LIST_POLL_MS : false) : false,
  });
}

/** Relevé de la liste des zones depuis l'accueil, tant que l'une se prépare. */
export const LIST_POLL_MS = 5_000;

/** Espace de stockage de la licence (jauge). */
export function useStorage(enabled = true) {
  return useQuery<StorageUsage>({ queryKey: STORAGE_KEY, queryFn: api.storage, enabled, staleTime: 30_000 });
}

/** État d'une zone. Avec `poll` (un seul observateur : le relevé de l'application, `useZoneWatch`), relevé tant
 *  qu'elle se prépare (ou s'étend) : 1,5 s, recul après des échecs, arrêt une fois prête, en échec, annulée ou
 *  supprimée. Les autres lecteurs partagent le même cache sans minuterie propre (sinon autant de relevés que de
 *  composants). */
export function useZone(id: string | null | undefined, poll = false) {
  return useQuery<Zone, ApiError>(zoneQueryOptions(id, poll));
}

/** Échecs consécutifs du relevé de chaque zone (voir `failureCounter`). */
export const zoneFailures = failureCounter();

/** Options de la requête d'une zone (séparées pour être éprouvées directement contre TanStack Query). */
export function zoneQueryOptions(id: string | null | undefined, poll = false) {
  const key = id ?? "";
  return {
    queryKey: zoneKey(key),
    queryFn: async () => {
      try {
        const zone = await background(() => api.zone(key));
        zoneFailures.succeeded(key);
        return zone;
      } catch (error) {
        zoneFailures.failed(key);
        throw error;
      }
    },
    enabled: Boolean(id),
    retry: false,
    staleTime: poll ? 0 : 5_000,
    refetchInterval: poll
      ? (query: { state: { data?: Zone; error: ApiError | null } }) =>
          pollDelay(query.state.data, zoneFailures.count(key), query.state.error?.status)
      : (false as const),
    refetchIntervalInBackground: poll,
  };
}

/** Zone libre affichée : celle dont le slug est le camp courant (dossier ouvert ou brouillon), null pour un camp. */
export function useOpenZone(poll = false) {
  const mapSlug = useUi((s) => s.mapSlug);
  const list = useZoneList();
  const listed = list.data?.find((z) => z.slug === mapSlug) ?? null;
  const zone = useZone(listed?.id, poll);
  return { zone: mapSlug && listed ? (zone.data ?? listed) : null, query: zone };
}

/** Clés du cache qui dépendent du terrain d'une zone : rafraîchies quand elle devient prête ou s'étend. */
const TERRAIN_KEYS = new Set([
  "maps",
  "map",
  "deployment",
  "orbat",
  "order",
  "order-layout",
  "order-fields",
  "briefing",
  "country-sheet",
  "aar",
  "tactical-roads",
  "layer",
  "camp-outline",
  "enemy-coa",
  "direx",
  "logistics",
  "coherence",
  "situation",
  "situation-exports",
  "exercises",
  "zones",
  "storage",
  // Ordres subordonnés : la génération attend le terrain prêt (raison « terrain en préparation »).
  "declination",
  // Réglage Météo : conditions possibles et verdict sur le choix suivent le climat (fin de préparation, extension,
  // climat changé).
  "weather-options",
]);

export function refreshTerrain(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ predicate: (q) => TERRAIN_KEYS.has(String(q.queryKey[0])) });
}

/** Verdict de la météo choisie du dossier sur le terrain qui vient d'être prêt : écart (texte de `weatherGapText`), ou
 *  choix confirmé tel quel (son libellé) ; rien des deux pour la météo saisonnière. */
export interface ReadyWeather {
  gap: string | null;
  confirmed: string | null;
}

/** Avis « Terrain prêt » : le fuseau calé sur les routes, et la météo de l'exercice (« Régler la météo » désigne le
 *  réglage de l'étape Terrain). Météo choisie sur le climat estimé (lieu nommé, terrain en préparation) : l'avis dit
 *  qu'elle est confirmée sur le terrain, ou l'écart du climat définitif (choix ajusté ou devenu inapplicable) ; aucun
 *  dossier n'est réécrit (le réglage montre l'écart et ses deux actions, l'alignement se fait à la prochaine
 *  écriture). Sans météo choisie : l'invitation à la régler si besoin. */
export function terrainReadyNotice(
  zone: Pick<Zone, "name">,
  weather: ReadyWeather,
  run: { frame: () => void; weather: () => void },
): Omit<Notice, "id"> {
  const placed = t("Le fuseau est calé sur les routes ; unités et LIMA sont en place.");
  return {
    text: t("Terrain prêt : {name}", { name: zone.name }),
    detail: weather.gap
      ? `${placed} ${weather.gap}`
      : weather.confirmed
        ? `${placed} ${t("Météo choisie confirmée sur ce terrain : {libelle}.", { libelle: weather.confirmed })}`
        : t("Le fuseau est calé sur les routes ; unités et LIMA sont en place. Réglez la météo de l'exercice à l'étape Terrain si besoin."),
    tone: weather.gap ? "warn" : "ok",
    action: { label: t("Voir le terrain"), run: run.frame },
    secondary: { label: t("Régler la météo"), run: run.weather },
  };
}

/** Verdict de la météo enregistrée du dossier ouvert sur le terrain qui vient d'être prêt (serveur, mêmes options que
 *  le réglage, mises en cache) : écart, ou choix confirmé ; rien sans météo choisie ni importée, en CENTAC, ou si le
 *  verdict n'a pas pu être lu (l'avis paraît alors sans lui). */
export async function readyWeather(queryClient: QueryClient, zone: Pick<Zone, "slug" | "status" | "climate">, exercise: Exercise | undefined): Promise<ReadyWeather> {
  const none = { gap: null, confirmed: null };
  const p = exercise?.parameters;
  const key = weatherKey(p);
  if (!p || !key || key === SEASONAL) return none;
  try {
    const options = await queryClient.fetchQuery({
      queryKey: weatherOptionsKey(zone.slug, zone, p.start_dtg, p.time_zone, key),
      queryFn: () =>
        api.weatherOptions(zone.slug, {
          start_dtg: p.start_dtg,
          time_zone: p.time_zone,
          weather_scenario: p.weather_scenario,
          weather_custom: p.weather_scenario === CUSTOM ? (p.weather_custom ?? null) : null,
        }),
    });
    const gap = weatherGap(options, key);
    if (gap) return { gap: weatherGapText(gap), confirmed: null };
    return { gap: null, confirmed: weatherState(p) === "custom" ? appliedWeatherLabel(key, null, options.regional_wind) : null };
  } catch {
    return none;
  }
}

/** Garde la liste des zones à jour d'un état relevé. */
export function storeZone(queryClient: QueryClient, zone: Zone) {
  queryClient.setQueryData<Zone>(zoneKey(zone.id), zone);
  queryClient.setQueryData<Zone[]>(ZONES_KEY, (list) =>
    list ? (list.some((z) => z.id === zone.id) ? list.map((z) => (z.id === zone.id ? zone : z)) : [...list, zone]) : list,
  );
}

/** Relevé au niveau de l'application (monté une fois, dans `App`) : la zone du dossier ouvert est suivie où que soit
 *  l'utilisateur ; quand elle devient prête, le terrain, le dispositif et l'ordre sont relus, et l'avis « Terrain
 *  prêt » propose « Voir la zone » ; en échec, l'avis désigne l'action attendue (`zoneWait`). */
export function useZoneWatch() {
  const queryClient = useQueryClient();
  const { zone } = useOpenZone(true);
  const previous = useRef<Zone | null>(null);
  useEffect(() => {
    const before = previous.current;
    previous.current = zone;
    if (!zone || !before || before === zone) return;
    if (zone !== before && zone.id === before.id) storeZone(queryClient, zone);
    const ui = useUi.getState();
    const show = (target: string) => {
      ui.setView("manoeuvre");
      designateSoon(`[data-attention="${target}"]`);
    };
    if (becameReady(before, zone)) {
      refreshTerrain(queryClient);
      // Dossier importé (brigade ou division) qui attendait son terrain : l'avis propose l'étape suivante, générer
      // les ordres des subordonnés (décision « pour l'import seulement, ils sont générés directement »).
      const opened = ui.exerciseId;
      if (opened && takeDeclineAfterTerrain(opened)) {
        const level = queryClient.getQueryData<Exercise[]>(["exercises"])?.find((e) => e.id === opened)?.parameters.level;
        ui.notify({
          text: t("Terrain prêt : {name}", { name: zone.name }),
          detail: t("Prochaine étape : générer les ordres des subordonnés."),
          tone: "ok",
          action: {
            label: actionLabel(level, "generate"),
            run: () => {
              ui.setView("declination");
              designateSoon(`[data-attention="${GENERATE_TARGET}"]`, "auto", 600);
            },
          },
        });
        return;
      }
      // Climat désormais définitif : la météo de l'exercice se règle à l'étape Terrain ; un choix devenu
      // inapplicable est dit (verdict du serveur), sans rien réécrire.
      const exercise = queryClient.getQueryData<Exercise[]>(["exercises"])?.find((e) => e.id === opened);
      void readyWeather(queryClient, zone, exercise?.map_slug === zone.slug ? exercise : undefined).then((weather) =>
        ui.notify(
          terrainReadyNotice(zone, weather, {
            frame: () => {
              show(ZONE_TARGETS.card);
              if (zone.bbox) ui.requestFlyTo([(zone.bbox[0] + zone.bbox[2]) / 2, (zone.bbox[1] + zone.bbox[3]) / 2], 9);
            },
            weather: showTerrainWeather,
          }),
        ),
      );
    } else if (becameSuperseded(before, zone)) {
      // Un autre terrain de la licence est passé en priorité (demande d'un autre poste) : reprise proposée.
      ui.notify({
        text: t("Préparation interrompue : un autre terrain est passé en priorité."),
        tone: "info",
        action: { label: t("Reprendre la préparation"), run: () => show(ZONE_TARGETS.retry) },
      });
    } else if (becameFailed(before, zone)) {
      // L'action proposée suit la cause : relancer, libérer de l'espace, ou supprimer une zone sans terre (la relance
      // échouerait à l'identique).
      const wait = zoneWait(zone);
      const action =
        wait === "remove"
          ? { label: t("Supprimer le terrain"), run: () => show(ZONE_TARGETS.remove) }
          : wait === "storage"
            ? { label: t("Libérer de l'espace"), run: () => show(ZONE_TARGETS.storage) }
            : { label: t("Réessayer"), run: () => show(ZONE_TARGETS.retry) };
      ui.notify({
        text: zone.error_message ? tServer(zone.error_message) : t("La préparation n'a pas abouti."),
        tone: "error",
        action,
      });
    }
  }, [zone, queryClient]);
}

/** Texte de l'avis d'une préparation interrompue par la demande qui vient de passer en priorité (le nom seulement
 *  quand le compte voit le terrain). */
export function interruptionNotice(item: ZoneInterruption): string {
  return item.name
    ? t("La préparation du terrain « {nom} » est interrompue : ce terrain passe en priorité.", { nom: item.name })
    : t("La préparation d'un autre terrain est interrompue : ce terrain passe en priorité.");
}

/** Demande passée en priorité (création, extension, relance) : un avis fluide par préparation interrompue, sans
 *  fenêtre de confirmation (décision d'Alexis du 11/10/2026), et les terrains interrompus relus (liste, fiche). */
export function announceInterrupted(queryClient: QueryClient, interrupted: ZoneInterruption[] | undefined): void {
  if (!interrupted?.length) return;
  const notify = useUi.getState().notify;
  for (const item of interrupted) {
    notify({ text: interruptionNotice(item), tone: "info" });
    if (item.id) void queryClient.invalidateQueries({ queryKey: zoneKey(item.id) });
  }
  void queryClient.invalidateQueries({ queryKey: ZONES_KEY });
}

/** Écritures d'une zone : chacune met à jour l'état en cache ; un refus 429 bloque « Réessayer » jusqu'au délai
 *  conseillé (`Retry-After`). Relance et extension passent en priorité (`announceInterrupted`). */
export function useZoneActions(zone: Zone | null) {
  const queryClient = useQueryClient();
  const blockRetry = useZoneDraw((s) => s.blockRetry);
  const done = (next: Zone) => {
    storeZone(queryClient, next);
    void queryClient.invalidateQueries({ queryKey: STORAGE_KEY });
  };
  const onRefused = (error: unknown) => {
    if (zone && error instanceof ApiError && error.status === 429) blockRetry(zone.id, retryUntil(error.retryAfterMs));
  };
  const rename = useMutation({ mutationFn: (name: string) => api.updateZone(zone!.id, { name }), onSuccess: done });
  const climate = useMutation({
    mutationFn: (value: string) => api.updateZone(zone!.id, { climate: value }),
    onSuccess: (next) => {
      done(next);
      refreshTerrain(queryClient); // l'ordre est recomposé à son ouverture
    },
  });
  const prioritised = (next: Zone) => {
    done(next);
    announceInterrupted(queryClient, next.interrupted);
  };
  const retry = useMutation({ mutationFn: () => api.retryZone(zone!.id), onSuccess: prioritised, onError: onRefused });
  const cancel = useMutation({ mutationFn: () => api.cancelZone(zone!.id), onSuccess: done });
  const extend = useMutation({
    mutationFn: (bbox: BBox) => api.extendZone(zone!.id, bbox),
    onSuccess: prioritised,
    onError: onRefused,
  });
  const remove = useMutation({
    mutationFn: (force: boolean) => api.deleteZone(zone!.id, force),
    onSuccess: (result) => applyZoneDeleted(queryClient, zone!.id, result),
  });
  return { rename, climate, retry, cancel, extend, remove };
}

/** Dossiers supprimés (un par un ou purge de l'accueil) : le serveur supprime avec eux les terrains libres que plus
 *  rien ne cite, ce qui rend les quotas de la licence ; liste des terrains, cartes et jauge de l'espace relues. */
export function refreshAfterDossiersRemoved(queryClient: QueryClient): void {
  for (const queryKey of [ZONES_KEY, ["maps"], STORAGE_KEY]) void queryClient.invalidateQueries({ queryKey });
}

/** Zone supprimée (forcée ou non) : retirée des listes ; les dossiers détachés n'ont plus de zone, avec leur nouvelle
 *  version, dans la liste des dossiers (accueil, version de la prochaine écriture) sans attendre sa relecture ; le
 *  dossier ouvert détaché perd sa carte ; le terrain, les dossiers et la jauge sont relus. */
export function applyZoneDeleted(queryClient: QueryClient, zoneId: string, result: ZoneDeleted): void {
  queryClient.setQueryData<Zone[]>(ZONES_KEY, (list) => list?.filter((z) => z.id !== zoneId));
  queryClient.removeQueries({ queryKey: zoneKey(zoneId) });
  const detached = new Map(result.detached.map((d) => [d.id, d]));
  if (detached.size > 0) {
    queryClient.setQueryData<Exercise[]>(["exercises"], (list) =>
      list?.map((e) => {
        const d = detached.get(e.id);
        return d ? { ...e, version: d.version, map_slug: null, map_name: null } : e;
      }),
    );
  }
  const ui = useUi.getState();
  // Dossier ouvert détaché de la zone : il n'a plus de zone, la carte se vide.
  if (ui.exerciseId && detached.has(ui.exerciseId)) {
    ui.setMapSlug(null);
    ui.setDeploymentParams(null);
  }
  refreshTerrain(queryClient);
}

/** Création de la zone d'un dossier (« Préparer le terrain ») : le dossier est rattaché (version + 1), la carte passe
 *  sur la zone, la liste des zones et le dossier en cache sont mis à jour. */
export function useCreateZone() {
  const queryClient = useQueryClient();
  const blockRetry = useZoneDraw((s) => s.blockRetry);
  return useMutation<ZoneCreated, unknown, { name: string; axis: ZoneAxis; exercise: Pick<Exercise, "id" | "version"> }>({
    mutationFn: (body) => api.createZone(body),
    onSuccess: (created) => {
      adoptCreated(queryClient, created);
      announceInterrupted(queryClient, created.interrupted);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 429) blockRetry("create", retryUntil(error.retryAfterMs));
      // Dossier modifié ailleurs (412) : sa version est relue, la demande peut repartir.
      if (error instanceof ApiError && error.status === 412) void queryClient.invalidateQueries({ queryKey: ["exercises"] });
    },
  });
}

/** Recréation de la zone gardée dans le dossier (import ou réception refusés par la file). */
export function useRecreateZone() {
  const queryClient = useQueryClient();
  const blockRetry = useZoneDraw((s) => s.blockRetry);
  return useMutation<ZoneCreated, unknown, Pick<Exercise, "id" | "version">>({
    mutationFn: (ex) => api.recreateZone(ex),
    onSuccess: (created) => adoptCreated(queryClient, created),
    onError: (error) => {
      if (error instanceof ApiError && error.status === 429) blockRetry("create", retryUntil(error.retryAfterMs));
      // Dossier modifié ailleurs (412) : sa version est relue, la demande peut repartir.
      if (error instanceof ApiError && error.status === 412) void queryClient.invalidateQueries({ queryKey: ["exercises"] });
    },
  });
}

function adoptCreated(queryClient: QueryClient, created: ZoneCreated) {
  const { exercise, ...zone } = created;
  storeZone(queryClient, zone);
  queryClient.setQueryData<Zone[]>(ZONES_KEY, (list) => (list ? list : [zone]));
  if (exercise) {
    queryClient.setQueryData<Exercise[]>(["exercises"], (list) =>
      list?.map((e) => (e.id === exercise.id ? { ...e, version: exercise.version, map_slug: zone.slug } : e)),
    );
    const ui = useUi.getState();
    if (ui.exerciseId === exercise.id) {
      ui.setExercise(exercise.id, zone.slug);
      // Dispositif affiché recalculé pour la zone (celui du camp précédent, ou aucun, est oublié).
      ui.setDeploymentParams(null);
    }
    if (exercise.notices.length) ui.notify({ text: tServer(exercise.notices[0]!), detail: exercise.notices.slice(1).map(tServer).join(" "), tone: "info" });
  }
  void queryClient.invalidateQueries({ queryKey: ["exercises"] });
  void queryClient.invalidateQueries({ queryKey: ["maps"] });
  void queryClient.invalidateQueries({ queryKey: STORAGE_KEY });
  useZoneDraw.getState().clearPending();
}
